import React, { useState, useMemo } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { usePermissions } from "@/hooks/usePermissions";
import { useProjectWorkOrders, useWorkOrderLines, useProjectContracts, useContractSovLines } from "@/hooks/useProjectContracts";
import { useVendors, getComplianceStatus } from "@/hooks/useVendors";
import { useUnitSchedule, useUnitScopeItems } from "@/hooks/useUnitSchedule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Plus, FileText, ArrowLeft, AlertTriangle, ChevronDown, ChevronRight, Pencil, Ban, Lock, MoreVertical, Zap, Receipt, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { WOVoidModal, WOCloseModal } from "./WOVoidCloseModals";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { generateWONumber } from "@/lib/naming";
import { GenerateWODialog } from "./GenerateWODialog";
import { WOExpandedContent } from "./WOExpandedContent";
import { WOAssignmentGrid, cellKey, parseKey, type CellKey } from "./WOAssignmentGrid";
import { WOCreateBillDialog } from "./WOCreateBillDialog";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtRate = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);
const pct = (n: number) => `${n.toFixed(1)}%`;

function VendorAvatar({ name, photoUrl }: { name: string; photoUrl?: string | null }) {
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  if (photoUrl) return <img src={photoUrl} alt={name} className="w-7 h-7 rounded-full object-cover shrink-0" />;
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ backgroundColor: `hsl(${hue}, 55%, 45%)` }}>
      {initials}
    </div>
  );
}

function ComplianceDot({ level }: { level: "green" | "yellow" | "red" }) {
  const colors = { green: "bg-green-500", yellow: "bg-yellow-500", red: "bg-destructive" };
  return <span className={cn("inline-block w-2.5 h-2.5 rounded-full shrink-0", colors[level])} />;
}

interface Props {
  projectId: string;
  project?: any;
  onMutate: () => void;
}

export const ProjectWOTab = ({ projectId, project, onMutate }: Props) => {
  const isMobile = useIsMobile();
  const { can, isAdmin } = usePermissions();
  const woPagination = usePagination();
  const { data: workOrders, refetch: refetchWOs } = useProjectWorkOrders(projectId, woPagination);
  const { data: woLines, refetch: refetchWoLines } = useWorkOrderLines(projectId);
  const { data: contracts } = useProjectContracts(projectId);
  const contract = contracts?.[0];
  const { data: sovLines } = useContractSovLines(contract?.id);
  const { data: vendors } = useVendors();
  const { data: units } = useUnitSchedule(projectId);
  const { data: scopeItems } = useUnitScopeItems(projectId);

  const hasUnits = (units ?? []).length > 0;
  const billingType = project?.billing_type ?? "unit_based";

  const [assignOpen, setAssignOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [selectedCells, setSelectedCells] = useState<Set<CellKey>>(new Set());
  // Legacy SOV-only state
  const [selectedSovLineIds, setSelectedSovLineIds] = useState<Set<string>>(new Set());
  const [rates, setRates] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [generateWO, setGenerateWO] = useState<any>(null);
  const [expandedWOs, setExpandedWOs] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem(`wo_expanded_${projectId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [bulkRate, setBulkRate] = useState("");
  const [assignQty, setAssignQty] = useState<Record<string, string>>({});
  const [rateOverrideReasons, setRateOverrideReasons] = useState<Record<string, string>>({});
  const [sellRateOverrides, setSellRateOverrides] = useState<Record<string, string>>({});
  // Edit WO state
  const [editWO, setEditWO] = useState<any>(null);
  const [editWODesc, setEditWODesc] = useState("");
  const [editWOVendor, setEditWOVendor] = useState("");
  const [editWOBusy, setEditWOBusy] = useState(false);
  const [editWOReassignCheck, setEditWOReassignCheck] = useState<{ allowed: boolean; reason?: string } | null>(null);
  // WO type for new WOs
  const [woType, setWoType] = useState<string>("blanket");
  const [woPhase, setWoPhase] = useState("");
  // Existing WO detection
  const [existingWOChoice, setExistingWOChoice] = useState<"add" | "new" | null>(null);
  // Add units to existing WO
  const [addUnitsWO, setAddUnitsWO] = useState<any>(null);
  // Void/Close modals
  const [voidWO, setVoidWO] = useState<any>(null);
  const [closeWO, setCloseWO] = useState<any>(null);
  const [woVoidableCache, setWoVoidableCache] = useState<Record<string, boolean>>({});
  // Hard delete voided WO
  const [deleteWO, setDeleteWO] = useState<any>(null);
  const [deletingWO, setDeletingWO] = useState(false);
  // Create Bill dialog
  const [billWO, setBillWO] = useState<any>(null);

  const canEdit = isAdmin || can("edit_work_orders");

  const woCostBySovLine = useMemo(() => {
    const map: Record<string, number> = {};
    (woLines ?? []).forEach((wol: any) => {
      const wo = wol.work_orders;
      if (wo && wo.status !== "rejected" && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed") {
        if (wol.status !== "voided" && wol.status !== "released") {
          map[wol.sov_line_id] = (map[wol.sov_line_id] || 0) + Number(wol.quantity) * Number(wol.contract_rate);
        }
      }
    });
    return map;
  }, [woLines]);

  const woQtyBySovLine = useMemo(() => {
    const map: Record<string, number> = {};
    (woLines ?? []).forEach((wol: any) => {
      const wo = wol.work_orders;
      if (wo && wo.status !== "rejected" && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed") {
        if (wol.status !== "voided" && wol.status !== "released") {
          map[wol.sov_line_id] = (map[wol.sov_line_id] || 0) + Number(wol.quantity);
        }
      }
    });
    return map;
  }, [woLines]);

  const refreshAll = () => { refetchWOs(); refetchWoLines(); onMutate(); };

  // --- Edit WO handler ---
  const handleEditWO = (wo: any) => {
    setEditWO(wo);
    setEditWODesc(wo.description ?? "");
    setEditWOVendor(wo.contractor_id ?? "");
    setEditWOReassignCheck(null);
  };

  const handleEditWOVendorChange = async (newVendorId: string) => {
    setEditWOVendor(newVendorId);
    if (!editWO) return;
    if (newVendorId === (editWO.contractor_id ?? "")) {
      setEditWOReassignCheck(null);
      return;
    }
    try {
      const result = await canReassignVendor(editWO.id);
      setEditWOReassignCheck(result);
    } catch (e: any) {
      setEditWOReassignCheck({ allowed: false, reason: e.message });
    }
  };

  const handleSaveEditWO = async () => {
    if (!editWO) return;
    setEditWOBusy(true);
    try {
      const vendorChanged = editWOVendor !== (editWO.contractor_id ?? "");

      // If vendor changed, re-validate before save (prevent race)
      if (vendorChanged) {
        const check = await canReassignVendor(editWO.id);
        if (!check.allowed) {
          toast.error(check.reason ?? "Vendor cannot be reassigned.");
          setEditWOBusy(false);
          return;
        }
      }

      const { data: companyId } = await supabase.rpc("my_company_id");
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const oldVendor = vendors?.find(v => v.id === editWO.contractor_id);
      const newVendor = vendors?.find(v => v.id === editWOVendor);

      if (vendorChanged) {
        // Step 1: Generate new WO number
        const newNumber = await generateWONumber(projectId, editWOVendor);
        const oldNumber = editWO.number;

        // Step 2: Append reassignment note to description
        const reassignNote = `\nVendor reassigned from ${oldVendor?.name ?? "unknown"} to ${newVendor?.name ?? "unknown"}`;
        const updatedDesc = (editWODesc || editWO.description || "") + reassignNote;

        // Step 3: Update work order — vendor + number + description atomically
        const { error } = await supabase.from("work_orders").update({
          contractor_id: editWOVendor || null,
          number: newNumber,
          description: updatedDesc,
        } as any).eq("id", editWO.id);
        if (error) throw error;

        // Step 4: Audit log (best-effort — don't block UI refresh)
        try {
          await supabase.from("ops_audit_log").insert({
            company_id: companyId,
            table_name: "work_orders",
            record_id: editWO.id,
            action: "vendor_reassigned",
            old_status: editWO.status,
            new_status: editWO.status,
            changed_by: userId,
            metadata: {
              old_vendor_id: editWO.contractor_id,
              new_vendor_id: editWOVendor,
              old_vendor_name: oldVendor?.name ?? null,
              new_vendor_name: newVendor?.name ?? null,
              old_work_order_number: oldNumber,
              new_work_order_number: newNumber,
            },
          } as any);
        } catch (auditErr) {
          console.error("Audit log failed:", auditErr);
        }

        toast.success(`Work order reassigned: ${oldNumber} → ${newNumber}`);
      } else {
        // No vendor change — simple description update
        const updates: any = { description: editWODesc };
        const { error } = await supabase.from("work_orders").update(updates).eq("id", editWO.id);
        if (error) throw error;

        // Audit if description changed
        if (editWODesc !== (editWO.description ?? "")) {
          await supabase.from("ops_audit_log").insert({
            company_id: companyId,
            table_name: "work_orders",
            record_id: editWO.id,
            action: "wo_edited",
            old_status: editWO.status,
            new_status: editWO.status,
            changed_by: userId,
            metadata: {
              changes: [`description: "${editWO.description}" → "${editWODesc}"`],
            },
          } as any);
        }

        toast.success("Work order updated");
      }

      setEditWO(null);
      setEditWOReassignCheck(null);
      refreshAll();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setEditWOBusy(false);
    }
  };

  // --- Guard check: can this WO be voided? ---
  const checkVoidable = async (woId: string): Promise<boolean> => {
    if (woVoidableCache[woId] !== undefined) return woVoidableCache[woId];
    try {
      const woLineIds = (woLines ?? []).filter((wol: any) => wol.work_order_id === woId).map((wol: any) => wol.id);
      if (woLineIds.length === 0) {
        setWoVoidableCache(prev => ({ ...prev, [woId]: true }));
        return true;
      }
      const { count } = await supabase
        .from("wo_draw_lines")
        .select("*", { count: "exact", head: true })
        .in("work_order_line_id", woLineIds);
      const voidable = (count ?? 0) === 0;
      setWoVoidableCache(prev => ({ ...prev, [woId]: voidable }));
      return voidable;
    } catch {
      return false;
    }
  };

  // --- Guard check: can this WO's vendor be reassigned? ---
  const canReassignVendor = async (woId: string): Promise<{ allowed: boolean; reason?: string }> => {
    const linesForWo = (woLines ?? []).filter((wol: any) => wol.work_order_id === woId);

    const hasBlockedLineStatus = linesForWo.some(
      (wol: any) => wol.status === "billed" || wol.status === "complete"
    );
    if (hasBlockedLineStatus) {
      return { allowed: false, reason: "Work has already been billed or completed." };
    }

    const { count: billCount, error: billError } = await supabase
      .from("bills")
      .select("id", { count: "exact", head: true })
      .eq("work_order_id", woId);
    if (billError) throw billError;
    if ((billCount ?? 0) > 0) {
      return { allowed: false, reason: "Bills already exist for this work order." };
    }

    const woLineIds = linesForWo.map((wol: any) => wol.id).filter(Boolean);
    if (woLineIds.length > 0) {
      const { count: drawCount, error: drawError } = await supabase
        .from("wo_draw_lines")
        .select("id", { count: "exact", head: true })
        .in("work_order_line_id", woLineIds);
      if (drawError) throw drawError;
      if ((drawCount ?? 0) > 0) {
        return { allowed: false, reason: "Draw activity exists for this work order." };
      }
    }

    return { allowed: true };
  };

  const handleVoidOrClose = async (wo: any) => {
    const canVoid = await checkVoidable(wo.id);
    if (canVoid) {
      setVoidWO(wo);
    } else {
      setCloseWO(wo);
    }
  };

  const handleDeleteWO = async () => {
    if (!deleteWO) return;
    setDeletingWO(true);
    try {
      const { error } = await supabase.rpc("hard_delete_work_order" as any, { p_wo_id: deleteWO.id });
      if (error) throw error;
      toast.success(`${deleteWO.number} permanently deleted`);
      setDeleteWO(null);
      refreshAll();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to delete work order");
    } finally {
      setDeletingWO(false);
    }
  };

  const handleNewWO = () => {
    if (!contract || !(sovLines ?? []).length) {
      toast.error("Create a contract with SOV lines first");
      return;
    }
    setStep(1);
    setSelectedVendorId("");
    setSelectedCells(new Set());
    setSelectedSovLineIds(new Set());
    setRates({});
    setAssignQty({});
    setSellRateOverrides({});
    setWoType("blanket");
    setWoPhase("");
    setExistingWOChoice(null);
    setAddUnitsWO(null);
    setAssignOpen(true);
  };

  // Detect existing open blanket WO for selected vendor
  const existingOpenWO = useMemo(() => {
    if (!selectedVendorId) return null;
    return (workOrders ?? []).find((wo: any) =>
      wo.contractor_id === selectedVendorId &&
      wo.status !== "cancelled" && wo.status !== "rejected" &&
      (wo.wo_type === "blanket" || !wo.wo_type)
    );
  }, [selectedVendorId, workOrders]);

  // Handle "Add Units" to existing WO
  const handleAddUnitsToWO = (wo: any) => {
    if (!contract || !(sovLines ?? []).length) {
      toast.error("No contract/SOV lines");
      return;
    }
    setAddUnitsWO(wo);
    setSelectedVendorId(wo.contractor_id);
    setSelectedCells(new Set());
    setRates({});
    setAssignQty({});
    setSellRateOverrides({});
    setAssignOpen(true);
    setStep(1);
    setExistingWOChoice("add");
  };

  const selectedVendor = vendors?.find(v => v.id === selectedVendorId);
  const vendorCompliance = selectedVendor ? getComplianceStatus(selectedVendor) : null;

  // For SOV-only mode (no units)
  const availableSovLines = useMemo(() => {
    return (sovLines ?? []).map((line: any) => {
      const sovValue = Number(line.scheduled_value);
      const committed = woCostBySovLine[line.id] || 0;
      const remaining = sovValue - committed;
      const sovQty = Number(line.quantity) || 0;
      const assignedQty = woQtyBySovLine[line.id] || 0;
      const remainingQty = sovQty - assignedQty;
      // Fully assigned only when BOTH dollar value and quantity are exhausted
      const fullyAssigned = remaining <= 0 && remainingQty <= 0;
      return { ...line, committed, remaining, fullyAssigned };
    });
  }, [sovLines, woCostBySovLine, woQtyBySovLine]);

  const selectedLines = availableSovLines.filter(l => selectedSovLineIds.has(l.id));
  const selectableLines = availableSovLines.filter(l => !l.fullyAssigned);

  const toggleLine = (id: string) => {
    if (!selectedVendorId) { toast.error("Select a contractor first"); return; }
    const line = availableSovLines.find(l => l.id === id);
    if (line?.fullyAssigned) return;
    setSelectedSovLineIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const toggleAll = () => {
    if (!selectedVendorId) { toast.error("Select a contractor first"); return; }
    if (selectedSovLineIds.size === selectableLines.length) setSelectedSovLineIds(new Set());
    else setSelectedSovLineIds(new Set(selectableLines.map(l => l.id)));
  };

  // Grid cell toggle for unit-based
  const handleToggleCell = (key: CellKey) => {
    setSelectedCells(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectionChange = (keys: CellKey[]) => setSelectedCells(new Set(keys));

  // Build rate data from grid selection
  const gridSovLineIds = useMemo(() => {
    const ids = new Set<string>();
    selectedCells.forEach(key => { const { sovLineId } = parseKey(key); ids.add(sovLineId); });
    return Array.from(ids);
  }, [selectedCells]);

  const gridSelectedLines = useMemo(() => {
    return gridSovLineIds.map(id => availableSovLines.find(l => l.id === id)).filter(Boolean) as any[];
  }, [gridSovLineIds, availableSovLines]);

  const goToStep2 = () => {
    const linesToUse = hasUnits ? gridSelectedLines : selectedLines;
    const newRates: Record<string, string> = {};
    const newQtys: Record<string, string> = {};

    linesToUse.forEach((line: any) => {
      const sellRate =
        Number(line.labor_unit_price ?? 0) + Number(line.material_unit_price ?? 0) > 0
          ? Number(line.labor_unit_price ?? 0) + Number(line.material_unit_price ?? 0)
          : Number(line.unit_price ?? 0);

      // Pre-fill rate from labor_vendor_rate (AP labor cost), fall back to sub_unit_price, then labor_unit_price
      const apLabor = Number(line.labor_vendor_rate ?? 0);
      const subRate = Number(line.sub_unit_price ?? 0);
      if (apLabor > 0) {
        newRates[line.id] = String(apLabor);
      } else if (subRate > 0) {
        newRates[line.id] = String(subRate);
      } else {
        const subLines = Array.isArray(line.sub_lines) ? line.sub_lines : [];
        const laborSubs = subLines.filter((sl: any) => sl.type === "labor" && sl.cost_price > 0);
        if (laborSubs.length > 0) {
          const avg = laborSubs.reduce((s: number, sl: any) => s + Number(sl.cost_price), 0) / laborSubs.length;
          newRates[line.id] = String(Math.round(avg * 100) / 100);
        } else {
          newRates[line.id] = sellRate > 0 ? String(sellRate) : "";
        }
      }

      // Bug 2 fix: initialize assignQty with full available quantity (user can reduce)
      if (hasUnits) {
        let qty = 0;
        selectedCells.forEach(key => {
          const { sovLineId, unitId } = parseKey(key);
          if (sovLineId === line.id) {
            const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
            qty += si ? Number(si.quantity) : 0;
          }
        });
        newQtys[line.id] = String(qty);
      } else {
        newQtys[line.id] = String(Number(line.quantity));
      }
    });

    setRates(newRates);
    setAssignQty(newQtys);
    setSellRateOverrides({});
    setStep(2);
  };

  // Helper: resolve effective sell rate for a line, considering overrides
  const getEffectiveSellRate = (line: any): number => {
    if (sellRateOverrides[line.id] !== undefined) return Number(sellRateOverrides[line.id]) || 0;
    const combined = Number(line.labor_unit_price ?? 0) + Number(line.material_unit_price ?? 0);
    return combined > 0 ? combined : Number(line.unit_price ?? 0);
  };

  const applyRateToAll = (rate: string) => {
    const newRates: Record<string, string> = {};
    const linesToUse = hasUnits ? gridSelectedLines : selectedLines;
    linesToUse.forEach((l: any) => { newRates[l.id] = rate; });
    setRates(newRates);
  };

  const getMargin = (sovRate: number, myRate: number) => {
    if (sovRate <= 0) return null;
    if (myRate <= 0) return 100;
    return ((sovRate - myRate) / sovRate) * 100;
  };

  const rateLines = hasUnits ? gridSelectedLines : selectedLines;

  const allRatesEntered = rateLines.every((l: any) => {
    const val = rates[l.id];
    return val !== undefined && val !== "";
  });

  const zeroRateLines = rateLines.filter((l: any) => Number(rates[l.id]) === 0);

  const woTotalAll = rateLines.reduce((s: number, l: any) => {
    const r = Number(rates[l.id]) || 0;
    if (hasUnits) {
      // Unit-based: qty controlled by cell selection in Step 1
      let qty = 0;
      selectedCells.forEach(key => {
        const { sovLineId, unitId } = parseKey(key);
        if (sovLineId === l.id) {
          const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
          qty += si ? Number(si.quantity) : 0;
        }
      });
      return s + qty * r;
    }
    // SOV-only: use assignQty (partial assignment) if set, else full SOV qty
    const qty = assignQty[l.id] !== undefined && assignQty[l.id] !== ""
      ? Number(assignQty[l.id])
      : Number(l.quantity);
    return s + qty * r;
  }, 0);

  const sovBudgetAll = rateLines.reduce((s: number, l: any) => {
    const sellRate = getEffectiveSellRate(l);
    if (hasUnits) {
      let qty = 0;
      selectedCells.forEach(key => {
        const { sovLineId, unitId } = parseKey(key);
        if (sovLineId === l.id) {
          const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
          qty += si ? Number(si.quantity) : 0;
        }
      });
      return s + qty * sellRate;
    }
    return s + Number(l.quantity) * sellRate;
  }, 0);

  const overallMargin = sovBudgetAll > 0 ? ((sovBudgetAll - woTotalAll) / sovBudgetAll) * 100 : 0;

  const parseRateError = (msg: string): string | null => {
    if (msg.includes("RATE_EXCEEDS_SELL_RATE")) return null; // No longer a hard block
    if (msg.includes("RATE_OVERRIDE_NOT_APPROVED")) return "Rate exceeds estimated sub cost and requires approval from an authorized user.";
    return null;
  };

  const handleCreateWOs = async () => {
    if (!allRatesEntered) return toast.error("Enter a rate for every line (0 is allowed)");
    setBusy(true);
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) { setBusy(false); return toast.error("Company not found"); }
    const userId = (await supabase.auth.getUser()).data.user?.id;

    for (const line of rateLines) {
      const rate = Number(rates[line.id]);
      const sellRate = getEffectiveSellRate(line);
      const subRate = Number(line.sub_unit_price ?? 0);

      // Warning: rate >= sell rate (zero margin) — skip for pass-through
      const isPassthrough = !!line.is_passthrough;
      if (!isPassthrough && rate > 0 && sellRate > 0 && rate >= sellRate) {
        toast.warning(`Rate for ${line.description} matches or exceeds sell rate (${fmtRate(sellRate)}) — zero-margin assignment.`);
        // Log zero-margin assignment (best-effort)
        try {
          const uid = (await supabase.auth.getUser()).data.user?.id ?? null;
          if (companyId) {
            await supabase.from("ops_audit_log").insert({
              company_id: companyId,
              table_name: "work_order_lines",
              record_id: line.id,
              action: "zero_margin_assignment",
              changed_by: uid,
              new_status: `rate=${rate},sell=${sellRate}`,
            });
          }
        } catch {}
      }

      // Override gate: rate > estimated sub rate
      if (subRate > 0 && rate > subRate) {
        if (!rateOverrideReasons[line.id]?.trim()) {
          setBusy(false);
          return toast.error(`Rate for ${line.description} exceeds estimated sub rate (${fmtRate(subRate)}). Enter a variance reason.`);
        }
        if (!can("approve_rate_overrides") && !isAdmin) {
          setBusy(false);
          return toast.error("Rate override requires approval from an authorized user (approve_rate_overrides permission).");
        }
      }
    }

    // Persist any sell rate overrides to SOV lines before creating WO lines
    const sellRateUpdates = Object.entries(sellRateOverrides).filter(
      ([lineId, val]) => {
        const sl = (sovLines ?? []).find((s: any) => s.id === lineId);
        if (!sl) return false;
        const oldSell = Number(sl.labor_unit_price ?? 0) + Number(sl.material_unit_price ?? 0);
        return Number(val) !== oldSell && Number(val) > 0;
      }
    );
    for (const [lineId, val] of sellRateUpdates) {
      const newSellRate = Number(val);
      const sl = (sovLines ?? []).find((s: any) => s.id === lineId);
      const qty = Number(sl?.quantity ?? 0);
      await supabase
        .from("sov_lines")
        .update({
          labor_unit_price: newSellRate,
          material_unit_price: 0,
          unit_price: newSellRate,
          scheduled_value: qty * newSellRate,
        } as any)
        .eq("id", lineId);
      // Audit log
      await supabase.from("ops_audit_log").insert({
        company_id: companyId,
        table_name: "sov_lines",
        record_id: lineId,
        action: "sell_rate_correction",
        old_status: JSON.stringify({
          labor_unit_price: Number(sl?.labor_unit_price ?? 0),
          material_unit_price: Number(sl?.material_unit_price ?? 0),
          scheduled_value: Number(sl?.scheduled_value ?? 0),
        }),
        new_status: JSON.stringify({
          labor_unit_price: newSellRate,
          material_unit_price: 0,
          scheduled_value: qty * newSellRate,
        }),
        changed_by: userId,
      } as any);
    }

    // If adding to existing WO, just add lines
    if (addUnitsWO) {
      let woLineRows: any[] = [];
      selectedCells.forEach(key => {
        const { unitId, sovLineId } = parseKey(key);
        const sl = (sovLines ?? []).find((s: any) => s.id === sovLineId);
        const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
        if (!sl || !si) return;
        const rate = Number(rates[sovLineId]) || 0;
        const qty = Number(si.quantity);
        const sellRate = getEffectiveSellRate(sl);
        const subRate = Number(sl.sub_unit_price ?? 0);
        const marginPct = sellRate > 0 ? ((sellRate - rate) / sellRate) * 100 : 0;
        const isOverride = subRate > 0 && rate > subRate;
        woLineRows.push({
          work_order_id: addUnitsWO.id,
          sov_line_id: sovLineId,
          unit_id: unitId,
          company_id: companyId,
          description: sl.description,
          quantity: qty,
          unit: sl.unit ?? "",
          contract_rate: rate,
          line_total: qty * rate,
          sov_rate: sellRate,
          margin_pct: Math.round(marginPct * 10) / 10,
          status: "pending",
          ...(isOverride ? {
             rate_source: "manual_override",
             rate_override_reason: rateOverrideReasons[sovLineId] ?? "",
             rate_approved_by: userId,
             rate_approved_at: new Date().toISOString(),
           } : { rate_source: "estimate" }),
         });
       });

      // INSERT the lines into the database
      const { error: insertErr } = await supabase
        .from("work_order_lines")
        .insert(woLineRows as any);
      if (insertErr) {
        setBusy(false);
        toast.error(insertErr.message);
        return;
      }

      // Audit log override lines
      const overrideLines = woLineRows.filter((r: any) => r.rate_source === "manual_override");
      for (const ol of overrideLines) {
        const sl = (sovLines ?? []).find((s: any) => s.id === ol.sov_line_id);
        await supabase.from("ops_audit_log").insert({
          company_id: companyId,
          table_name: "work_order_lines",
          record_id: addUnitsWO.id,
          action: "wo_rate_override",
          changed_by: userId,
          metadata: {
            sov_line_id: ol.sov_line_id,
            estimated_sub_rate: Number(sl?.sub_unit_price ?? 0),
            override_rate: ol.contract_rate,
            reason: ol.rate_override_reason,
            approver: userId,
          },
        } as any);
      }
      // Update WO total
      const newTotal = Number(addUnitsWO.amount) + woTotalAll;
      await supabase.from("work_orders").update({ amount: newTotal } as any).eq("id", addUnitsWO.id);
      setBusy(false);
      toast.success(`Added ${woLineRows.length} lines to ${addUnitsWO.number} | New total: ${fmt(newTotal)}`);
      setAssignOpen(false);
      setAddUnitsWO(null);
      refreshAll();
      return;
    }

    const vendorName = selectedVendor?.name ?? "Unknown";
    const woNumber = await generateWONumber(projectId, selectedVendorId, vendorName);

    // Calculate total value
    let totalValue = woTotalAll;

    const { data: wo, error: woErr } = await supabase.from("work_orders").insert({
      project_id: projectId,
      company_id: companyId,
      contractor_id: selectedVendorId,
      number: woNumber,
      description: `${vendorName} — ${project?.name ?? "Project"}`,
      amount: totalValue,
      wo_type: woType,
      phase: woPhase || null,
    } as any).select("id, number").single();

    if (woErr) { setBusy(false); return toast.error(woErr.message); }

    let woLineRows: any[];

    if (hasUnits) {
      // Unit-based: create one WO line per unit+SOV cell
      woLineRows = [];
      selectedCells.forEach(key => {
        const { unitId, sovLineId } = parseKey(key);
        const sl = (sovLines ?? []).find((s: any) => s.id === sovLineId);
        const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
        if (!sl || !si) return;
        const rate = Number(rates[sovLineId]) || 0;
        const qty = Number(si.quantity);
        const sellRate = getEffectiveSellRate(sl);
        const marginPct = sellRate > 0 ? ((sellRate - rate) / sellRate) * 100 : 0;
        const subRate = Number(sl.sub_unit_price ?? 0);
        const isOverride = subRate > 0 && rate > subRate;

        woLineRows.push({
          work_order_id: wo.id,
          sov_line_id: sovLineId,
          unit_id: unitId,
          company_id: companyId,
          description: sl.description,
          quantity: qty,
          unit: sl.unit ?? "",
          contract_rate: rate,
          line_total: qty * rate,
          sov_rate: sellRate,
          margin_pct: Math.round(marginPct * 10) / 10,
          status: "pending",
          ...(isOverride ? {
             rate_source: "manual_override",
             rate_override_reason: rateOverrideReasons[sovLineId] ?? "",
             rate_approved_by: userId,
             rate_approved_at: new Date().toISOString(),
           } : { rate_source: "estimate" }),
        });
      });
    } else {
      // SOV-only: one WO line per SOV line
      woLineRows = selectedLines.map((line: any) => {
        const rate = Number(rates[line.id]) || 0;
        // Use partial qty if user changed it, else full SOV line quantity
        const qty = assignQty[line.id] !== undefined && assignQty[line.id] !== ""
          ? Number(assignQty[line.id])
          : Number(line.quantity);
        const sellRate = getEffectiveSellRate(line);
        const subRate = Number(line.sub_unit_price ?? 0);
        const marginPct = sellRate > 0 ? ((sellRate - rate) / sellRate) * 100 : 0;
        const isOverride = subRate > 0 && rate > subRate;
        return {
          work_order_id: wo.id,
          sov_line_id: line.id,
          company_id: companyId,
          description: line.description,
          quantity: qty,
          unit: line.unit,
          contract_rate: rate,
          line_total: qty * rate,
          sov_rate: sellRate,
          margin_pct: Math.round(marginPct * 10) / 10,
          status: "pending",
          ...(isOverride ? {
             rate_source: "manual_override",
             rate_override_reason: rateOverrideReasons[line.id] ?? "",
             rate_approved_by: userId,
             rate_approved_at: new Date().toISOString(),
           } : { rate_source: "estimate" }),
        };
      });
    }

    const { error: wolErr } = await supabase.from("work_order_lines").insert(woLineRows as any);
    if (wolErr) {
      await supabase.from("work_orders").delete().eq("id", wo.id);
      setBusy(false);
      const friendly = parseRateError(wolErr.message);
      return toast.error(friendly ?? wolErr.message);
    }

    // Audit log override lines
    const overrideLines = woLineRows.filter((r: any) => r.rate_source === "manual_override");
    for (const ol of overrideLines) {
      const sl = (sovLines ?? []).find((s: any) => s.id === ol.sov_line_id);
      await supabase.from("ops_audit_log").insert({
        company_id: companyId,
        table_name: "work_order_lines",
        record_id: wo.id,
        action: "wo_rate_override",
        changed_by: userId,
        metadata: {
          sov_line_id: ol.sov_line_id,
          estimated_sub_rate: Number(sl?.sub_unit_price ?? 0),
          override_rate: ol.contract_rate,
          reason: ol.rate_override_reason,
          approver: userId,
        },
      } as any);
    }

    setBusy(false);
    toast.success(`Work Order ${wo.number} created — ${woLineRows.length} line items | Total: ${fmt(totalValue)}`);
    setAssignOpen(false);
    refreshAll();
  };

  const toggleWOExpand = (woId: string) => {
    setExpandedWOs(prev => {
      const next = new Set(prev);
      if (next.has(woId)) next.delete(woId); else next.add(woId);
      try { sessionStorage.setItem(`wo_expanded_${projectId}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  if (!contract) {
    return <div className="text-center py-12"><p className="text-muted-foreground">No work orders yet — create a contract first to assign work orders.</p></div>;
  }

  const stepOneHasSelection = hasUnits ? selectedCells.size > 0 : selectedSovLineIds.size > 0;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {can("create_work_orders") && <Button size="sm" onClick={handleNewWO}><Plus className="mr-1 h-4 w-4" />New Work Order</Button>}
      </div>

      {(() => {
        const allWOs = workOrders ?? [];
        const activeWOs = allWOs.filter((wo: any) => wo.status !== "voided" && wo.status !== "cancelled");
        const inactiveWOs = allWOs.filter((wo: any) => wo.status === "voided" || wo.status === "cancelled");

        if (!allWOs.length) {
          return (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-2">No work orders yet</p>
              <Button variant="outline" size="sm" onClick={handleNewWO}>Create your first Work Order</Button>
            </div>
          );
        }

        const renderWOCard = (wo: any, readOnly = false) => {
          const linesForWo = (woLines ?? []).filter((wol: any) => wol.work_order_id === wo.id);
          const activeLines = linesForWo.filter((wol: any) => wol.status !== "voided" && wol.status !== "released");
          const openLines = activeLines.filter((wol: any) => wol.status !== "billed");
          const billedLines = activeLines.filter((wol: any) => wol.status === "billed");
          const openTotal = openLines.reduce((s: number, wol: any) => s + Number(wol.quantity) * Number(wol.contract_rate), 0);
          const billedTotal = billedLines.reduce((s: number, wol: any) => s + Number(wol.billed_amount ?? (Number(wol.quantity) * Number(wol.contract_rate))), 0);
          const retainage = linesForWo.reduce((s: number, wol: any) => s + Number(wol.retainage_held), 0);
          const isExpanded = expandedWOs.has(wo.id);

          return (
            <Card key={wo.id} className={cn((wo.status === "voided" || wo.status === "cancelled") && "opacity-50 border-destructive/30", wo.status === "closed" && "opacity-70")}>
              <div className="flex flex-col md:flex-row md:items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors gap-2" onClick={() => toggleWOExpand(wo.id)}>
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <div>
                    <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                      <span className="font-mono text-sm font-semibold">{wo.number}</span>
                      <StatusBadge status={wo.status} />
                      {wo.status === "voided" && <Badge variant="destructive" className="text-[10px]">Voided</Badge>}
                      {wo.status === "closed" && <Badge variant="secondary" className="text-[10px]">Closed</Badge>}
                      {(wo.wo_type === "blanket" || !wo.wo_type) && <Badge variant="outline" className="text-[10px]">Blanket</Badge>}
                      {wo.wo_type === "phase" && <Badge variant="outline" className="text-[10px]">Phase</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">{wo.vendors?.name ?? wo.description ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {(() => {
                        const billedCount = activeLines.filter((wol: any) => wol.status === "billed").length;
                        const openCount = activeLines.length - billedCount;
                        const parts: string[] = [];
                        if (billedCount > 0) {
                          parts.push(`${openCount} open / ${billedCount} billed`);
                        } else {
                          parts.push(`${activeLines.length} line item${activeLines.length !== 1 ? "s" : ""}`);
                        }
                        if (activeLines.length < linesForWo.length) parts.push(`${linesForWo.length - activeLines.length} voided`);
                        return parts.join(" · ");
                      })()}{wo.phase ? ` · ${wo.phase}` : ""}
                      {wo.lines_released > 0 && <span> · {wo.lines_released} released</span>}
                      {wo.lines_voided > 0 && <span> · {wo.lines_voided} voided</span>}
                      {wo.void_reason && <span className="text-destructive"> · {wo.void_reason}</span>}
                      {wo.close_reason && <span> · {wo.close_reason}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="font-semibold">{fmt(openTotal)}</div>
                    <div className="text-xs text-muted-foreground">Billed: {fmt(billedTotal)} | Ret: {fmt(retainage)}</div>
                  </div>
                  {!readOnly && (
                    isMobile ? (
                      /* Mobile: single ⋮ menu with all actions */
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {hasUnits && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (wo.wo_type === "blanket" || !wo.wo_type) && (
                            <DropdownMenuItem onClick={() => handleAddUnitsToWO(wo)}>
                              <Plus className="h-3.5 w-3.5 mr-2" /> Add Units
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setGenerateWO(wo)}>
                            <FileText className="h-3.5 w-3.5 mr-2" /> WO Document
                          </DropdownMenuItem>
                          {canEdit && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (
                            <>
                              <DropdownMenuItem onClick={() => handleEditWO(wo)}>
                                <Pencil className="h-3.5 w-3.5 mr-2" /> Edit Work Order
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setBillWO(wo)}>
                                <Receipt className="h-3.5 w-3.5 mr-2" /> Create Bill
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setCloseWO(wo)}>
                                <Lock className="h-3.5 w-3.5 mr-2" /> Close Work Order
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-destructive" onClick={() => handleVoidOrClose(wo)}>
                                <Ban className="h-3.5 w-3.5 mr-2" /> Void Work Order
                              </DropdownMenuItem>
                            </>
                          )}
                          {canEdit && wo.status === "voided" && (
                            <DropdownMenuItem className="text-destructive" onClick={() => setDeleteWO(wo)}>
                              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Permanently
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      /* Desktop: inline buttons + overflow menu */
                      <>
                        {hasUnits && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (wo.wo_type === "blanket" || !wo.wo_type) && (
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleAddUnitsToWO(wo); }}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Add Units
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setGenerateWO(wo); }}>
                          <FileText className="h-4 w-4 mr-1" /> WO Document
                        </Button>
                        {(canEdit && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed") || (canEdit && wo.status === "voided") ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEdit && wo.status !== "cancelled" && wo.status !== "voided" && wo.status !== "closed" && (
                                <>
                                  <DropdownMenuItem onClick={() => handleEditWO(wo)}>
                                    <Pencil className="h-3.5 w-3.5 mr-2" /> Edit Work Order
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setBillWO(wo)}>
                                    <Receipt className="h-3.5 w-3.5 mr-2" /> Create Bill
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setCloseWO(wo)}>
                                    <Lock className="h-3.5 w-3.5 mr-2" /> Close Work Order
                                  </DropdownMenuItem>
                                  <DropdownMenuItem className="text-destructive" onClick={() => handleVoidOrClose(wo)}>
                                    <Ban className="h-3.5 w-3.5 mr-2" /> Void Work Order
                                  </DropdownMenuItem>
                                </>
                              )}
                              {canEdit && wo.status === "voided" && (
                                <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteWO(wo); }}>
                                  <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Permanently
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </>
                    )
                  )}
                </div>
              </div>
              {isExpanded && (
                <CardContent className="pt-0 pb-3">
                  <WOExpandedContent
                    wo={wo}
                    linesForWo={activeLines}
                    billed={billedTotal}
                    projectId={projectId}
                    project={project}
                    sovLines={sovLines ?? []}
                    onMutate={refreshAll}
                    billingType={billingType}
                  />
                </CardContent>
              )}
            </Card>
          );
        };

        return (
          <Tabs defaultValue="active" className="w-full">
            <TabsList>
              <TabsTrigger value="active">Active ({activeWOs.length})</TabsTrigger>
              <TabsTrigger value="cancelled">Cancelled / Voided ({inactiveWOs.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="active">
              <div className="space-y-3">
                {activeWOs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">All work orders are cancelled or voided.</p>
                ) : activeWOs.map((wo: any) => renderWOCard(wo))}
              </div>
            </TabsContent>
            <TabsContent value="cancelled">
              <div className="space-y-3">
                {inactiveWOs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No cancelled or voided work orders.</p>
                ) : inactiveWOs.map((wo: any) => renderWOCard(wo))}
              </div>
            </TabsContent>
          </Tabs>
        );
      })()}
      <PaginationControls pagination={woPagination} />

      {/* Assign WO Dialog */}
      <Dialog open={assignOpen} onOpenChange={(o) => { if (!o) setAssignOpen(false); }}>
        <DialogContent className={cn("max-h-[92vh] overflow-y-auto", hasUnits ? "w-[95vw] max-w-[95vw] md:min-w-[600px]" : "max-w-[720px]")} style={{ resize: "both", overflow: "auto" }}>
          {step === 1 ? (
            <>
              <DialogHeader>
                <DialogTitle>{addUnitsWO ? `Add Units to ${addUnitsWO.number}` : "New Work Order"}</DialogTitle>
                <DialogDescription>
                  {addUnitsWO ? `Adding units to existing blanket WO for ${addUnitsWO.vendors?.name}` : `Select subcontractor then ${hasUnits ? "pick unit × SOV cells" : "choose SOV lines"}`}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* WO Type selector (only for new WOs) */}
                {!addUnitsWO && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Type</label>
                    <div className="flex gap-2">
                      {(["blanket", "phase", "rework", "backcharge"] as const).map(t => (
                        <Button
                          key={t}
                          variant={woType === t ? "default" : "outline"}
                          size="sm"
                          onClick={() => setWoType(t)}
                          className="capitalize"
                        >
                          {t === "blanket" ? "● Blanket" : t}
                        </Button>
                      ))}
                    </div>
                    {woType === "phase" && (
                      <Input placeholder="Phase label (e.g. Phase 1 — Flooring)" value={woPhase} onChange={e => setWoPhase(e.target.value)} />
                    )}
                  </div>
                )}

                {/* Vendor selector */}
                {!addUnitsWO && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Subcontractor</label>
                    <Select value={selectedVendorId} onValueChange={(v) => { setSelectedVendorId(v); setExistingWOChoice(null); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select contractor">
                          {selectedVendor && (
                            <span className="flex items-center gap-2">
                              <VendorAvatar name={selectedVendor.name} photoUrl={(selectedVendor as any).profile_photo_url} />
                              <span>{selectedVendor.name}</span>
                              <ComplianceDot level={vendorCompliance?.level ?? "green"} />
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {(vendors ?? []).map((v) => {
                          const cs = getComplianceStatus(v);
                          const types = v.vendor_types || [v.vendor_type || "general"];
                          const typeLabel = types[0]?.replace(/_/g, " ") ?? "general";
                          return (
                            <SelectItem key={v.id} value={v.id}>
                              <span className="flex items-center gap-2">
                                <VendorAvatar name={v.name} photoUrl={(v as any).profile_photo_url} />
                                <span>{v.name}</span>
                                <ComplianceDot level={cs.level} />
                                <Badge variant="outline" className="text-[10px] capitalize">{typeLabel}</Badge>
                              </span>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedVendor && vendorCompliance && vendorCompliance.level !== "green" && (
                      <div className="flex items-start gap-2 p-2 rounded-md border border-yellow-200 dark:border-yellow-800 text-sm bg-yellow-50/50 dark:bg-yellow-950/50">
                        <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                        <div>
                          <span className="font-medium">{selectedVendor.name}</span>
                          <span className="text-muted-foreground"> — {vendorCompliance.issues.map(i => i.label).join(", ")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Existing WO detection */}
                {existingOpenWO && !addUnitsWO && existingWOChoice === null && woType === "blanket" && (
                  <div className="border rounded-md p-3 space-y-2 bg-muted/30">
                    <div className="flex items-start gap-2 text-sm">
                      <Zap className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <div>
                        <span className="font-medium">{selectedVendor?.name}</span> already has an open blanket WO:
                        <div className="font-mono text-xs mt-1">
                          {existingOpenWO.number} · {(woLines ?? []).filter((wol: any) => wol.work_order_id === existingOpenWO.id).length} lines assigned
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant={existingWOChoice === "add" ? "default" : "outline"} onClick={() => { setExistingWOChoice("add"); setAddUnitsWO(existingOpenWO); }}>
                        Add units to {existingOpenWO.number}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setExistingWOChoice("new")}>
                        Create new WO anyway
                      </Button>
                    </div>
                  </div>
                )}

                {/* Assignment Grid (unit-based) or SOV line picker (SOV-only) */}
                {selectedVendorId && (
                  hasUnits ? (
                    <WOAssignmentGrid
                      units={units ?? []}
                      sovLines={sovLines ?? []}
                      scopeItems={scopeItems ?? []}
                      existingWoLines={woLines ?? []}
                      contractorId={selectedVendorId}
                      selectedCells={selectedCells}
                      onToggleCell={handleToggleCell}
                      onSelectionChange={handleSelectionChange}
                      onUnassign={async (wolId: string) => {
                        // Delete the WO line to unassign
                        const { error } = await supabase
                          .from("work_order_lines")
                          .update({ status: "voided" } as any)
                          .eq("id", wolId);
                        if (error) throw error;
                        refreshAll();
                      }}
                    />
                  ) : (
                    /* SOV-only picker */
                    <div className="space-y-1">
                      <span className="text-sm font-medium">Select SOV Lines</span>
                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10">
                                <Checkbox
                                  checked={selectableLines.length > 0 && selectedSovLineIds.size === selectableLines.length}
                                  onCheckedChange={toggleAll}
                                />
                              </TableHead>
                              <TableHead>SOV Line</TableHead>
                              <TableHead>Qty</TableHead>
                              <TableHead>Unit</TableHead>
                              <TableHead className="text-right">Remaining</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {availableSovLines.map((line: any) => (
                              <TableRow key={line.id} className={cn(line.fullyAssigned && "opacity-50", selectedSovLineIds.has(line.id) && "bg-primary/5")}>
                                <TableCell>
                                  {line.fullyAssigned ? (
                                    <Badge variant="secondary" className="text-[10px]">Assigned</Badge>
                                  ) : (
                                    <Checkbox checked={selectedSovLineIds.has(line.id)} onCheckedChange={() => toggleLine(line.id)} />
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span className="font-mono text-xs text-muted-foreground mr-1">#{line.line_number}</span>
                                  <span className="font-medium text-sm">{line.description}</span>
                                </TableCell>
                                <TableCell className="font-mono text-sm">{Number(line.quantity).toLocaleString()}</TableCell>
                                <TableCell className="text-sm">{line.unit}</TableCell>
                                <TableCell className={cn("text-right text-sm font-medium", line.remaining <= 0 ? "text-destructive" : "")}>{fmt(line.remaining)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
                  <Button disabled={!selectedVendorId || !stepOneHasSelection} onClick={goToStep2}>
                    Next: Set Rates →
                  </Button>
                </div>
              </div>
            </>
          ) : (
            /* STEP 2 — Set Rates */
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="h-8 px-2"><ArrowLeft className="h-4 w-4" /></Button>
                  <div>
                    <DialogTitle>Set Rates</DialogTitle>
                    <DialogDescription>
                      {selectedVendor?.name} — {hasUnits ? `${selectedCells.size} unit-lines across ${gridSovLineIds.length} SOV line${gridSovLineIds.length !== 1 ? "s" : ""}` : `${selectedLines.length} line item${selectedLines.length > 1 ? "s" : ""}`}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Apply one rate to all:</span>
                  <Input type="number" step="0.01" min="0" className="w-28" placeholder="$/unit" value={bulkRate} onChange={e => setBulkRate(e.target.value)} />
                  <Button variant="outline" size="sm" disabled={!bulkRate && bulkRate !== "0"} onClick={() => applyRateToAll(bulkRate)}>Apply to All</Button>
                </div>

                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SOV Line</TableHead>
                        {hasUnits && <TableHead className="text-right">Units</TableHead>}
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead className="text-right">Sell Rate</TableHead>
                        <TableHead className="text-right">Est. Sub Rate</TableHead>
                        <TableHead className="text-right w-28">Rate</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rateLines.map((line: any) => {
                        const baseSellRate =
                          Number(line.labor_unit_price ?? 0) + Number(line.material_unit_price ?? 0) > 0
                            ? Number(line.labor_unit_price ?? 0) + Number(line.material_unit_price ?? 0)
                            : Number(line.unit_price ?? 0);
                        const sellRate = sellRateOverrides[line.id] !== undefined
                          ? Number(sellRateOverrides[line.id]) || 0
                          : baseSellRate;
                        const subRate = Number(line.sub_unit_price ?? 0);
                        const myRate = Number(rates[line.id]) || 0;
                        const margin = getMargin(sellRate, myRate);
                        const isPassthrough = !!line.is_passthrough;
                        const exceedsSellRate = !isPassthrough && myRate > 0 && myRate >= sellRate;
                        const exceedsSubRate = subRate > 0 && myRate > subRate && !exceedsSellRate;
                        const isZeroRate = rates[line.id] !== "" && myRate === 0;

                        // Count cells and qty for this SOV line
                        let cellCount = 0;
                        let totalQty = 0;
                        if (hasUnits) {
                          selectedCells.forEach(key => {
                            const { sovLineId, unitId } = parseKey(key);
                            if (sovLineId === line.id) {
                              cellCount++;
                              const si = (scopeItems ?? []).find((s: any) => s.unit_id === unitId && s.sov_line_id === sovLineId);
                              totalQty += si ? Number(si.quantity) : 0;
                            }
                          });
                        } else {
                          totalQty = Number(line.quantity);
                        }

                        return (
                          <React.Fragment key={line.id}>
                            <TableRow className={cn(
                              exceedsSellRate && "bg-destructive/5",
                              exceedsSubRate && "bg-yellow-50/50 dark:bg-yellow-950/30"
                            )}>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-sm">{line.description}</span>
                                  {isPassthrough && <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-950 dark:text-blue-400">Pass-through</Badge>}
                                  {isZeroRate && <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />}
                                </div>
                              </TableCell>
                              {hasUnits && <TableCell className="text-right font-mono text-sm">{cellCount}</TableCell>}
                              <TableCell className="text-right">
                                {hasUnits ? (
                                  <span className="font-mono text-sm">{totalQty.toLocaleString()}</span>
                                ) : (
                                  <div className="flex flex-col items-end gap-0.5">
                                    <Input
                                      type="number" step="0.01" min="0"
                                      max={totalQty}
                                      className="w-24 text-right h-7 text-sm"
                                      value={assignQty[line.id] ?? String(totalQty)}
                                      onChange={e => setAssignQty(prev => ({ ...prev, [line.id]: e.target.value }))}
                                    />
                                    <span className="text-[10px] text-muted-foreground">of {totalQty.toLocaleString()}</span>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-sm">{line.unit_of_measure ?? line.unit ?? ""}</TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number" step="0.01" min="0"
                                  className="w-24 text-right h-7 text-sm"
                                  value={sellRateOverrides[line.id] ?? String(sellRate)}
                                  onChange={e => setSellRateOverrides(prev => ({ ...prev, [line.id]: e.target.value }))}
                                />
                              </TableCell>
                              <TableCell className="text-right text-sm font-mono">{subRate > 0 ? fmtRate(subRate) : <span className="text-muted-foreground">—</span>}</TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number" step="0.01" min="0"
                                  className={cn("w-24 text-right", exceedsSellRate && "border-destructive text-destructive", exceedsSubRate && "border-yellow-500")}
                                  value={rates[line.id] ?? ""}
                                  onChange={e => setRates(prev => ({ ...prev, [line.id]: e.target.value }))}
                                />
                              </TableCell>
                              <TableCell className="text-right text-sm font-medium">
                                {margin !== null ? (
                                  <span className={cn(margin >= 25 ? "text-green-600" : margin >= 20 ? "text-yellow-600" : "text-destructive")}>
                                    {pct(margin)} {margin >= 25 ? "🟢" : margin >= 20 ? "🟡" : "🔴"}
                                  </span>
                                ) : "—"}
                              </TableCell>
                            </TableRow>
                            {/* Sell rate violation */}
                            {exceedsSellRate && (
                              <TableRow>
                                <TableCell colSpan={hasUnits ? 8 : 7} className="py-1 px-4">
                                  <div className="flex items-center gap-2 text-sm text-destructive">
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                    <span>Rate must be less than sell rate ({fmtRate(sellRate)}) — zero-margin pricing is not allowed</span>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                            {/* Sub rate override — requires reason */}
                            {exceedsSubRate && (
                              <TableRow>
                                <TableCell colSpan={hasUnits ? 8 : 7} className="py-1 px-4">
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-400">
                                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                      <span>Rate exceeds estimated sub rate ({fmtRate(subRate)}) — variance reason required</span>
                                      {!can("approve_rate_overrides") && !isAdmin && (
                                        <Badge variant="destructive" className="text-[10px]">No override authority</Badge>
                                      )}
                                    </div>
                                    <Textarea
                                      placeholder="Explain why this rate exceeds the estimated sub cost…"
                                      className="h-16 text-sm"
                                      value={rateOverrideReasons[line.id] ?? ""}
                                      onChange={e => setRateOverrideReasons(prev => ({ ...prev, [line.id]: e.target.value }))}
                                    />
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {zeroRateLines.length > 0 && (
                  <div className="flex items-start gap-2 p-3 rounded-md bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 text-sm">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    <span><strong>{zeroRateLines.length} line{zeroRateLines.length > 1 ? "s" : ""}</strong> ha{zeroRateLines.length > 1 ? "ve" : "s"} $0 rate.</span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm bg-muted/50 rounded-md p-3">
                  <div className="space-x-4">
                    <span>WO Total: <span className="font-semibold">{fmt(woTotalAll)}</span></span>
                    <span>SOV Budget: <span className="font-semibold">{fmt(sovBudgetAll)}</span></span>
                  </div>
                  <span>
                    Margin:{" "}
                    <span className={cn("font-semibold", overallMargin >= 25 ? "text-green-600" : overallMargin >= 20 ? "text-yellow-600" : "text-destructive")}>
                      {pct(overallMargin)} {overallMargin >= 25 ? "🟢" : overallMargin >= 20 ? "🟡" : "🔴"}
                    </span>
                  </span>
                </div>

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
                  <Button disabled={busy || !allRatesEntered} onClick={handleCreateWOs}>
                    {busy ? "Creating…" : `Create Work Order (${hasUnits ? selectedCells.size : selectedLines.length} lines)`}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {generateWO && project && (
        <GenerateWODialog
          open={!!generateWO}
          onOpenChange={(o) => !o && setGenerateWO(null)}
          workOrder={generateWO}
          woLines={woLines ?? []}
          project={project}
        />
      )}

      {/* Edit WO Dialog */}
      <Dialog open={!!editWO} onOpenChange={(o) => { if (!o) { setEditWO(null); setEditWOReassignCheck(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Work Order — {editWO?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subcontractor</label>
              <Select value={editWOVendor} onValueChange={handleEditWOVendorChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contractor" />
                </SelectTrigger>
                <SelectContent>
                  {(vendors ?? []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reassignment guard banners */}
            {editWOReassignCheck?.allowed === false && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
                <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Vendor cannot be changed</p>
                  <p className="text-muted-foreground">{editWOReassignCheck.reason} Close this work order and reassign remaining scope from the SOV.</p>
                </div>
              </div>
            )}
            {editWOReassignCheck?.allowed === true && (
              <div className="flex items-start gap-2 rounded-md border border-yellow-500/50 bg-yellow-500/10 p-3 text-sm">
                <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-700 dark:text-yellow-400">Vendor reassignment</p>
                  <p className="text-muted-foreground">The work order number will regenerate to reflect the new vendor ID. All identity fields will be updated.</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea value={editWODesc} onChange={(e) => setEditWODesc(e.target.value)} rows={3} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditWO(null); setEditWOReassignCheck(null); }}>Cancel</Button>
              <Button
                onClick={handleSaveEditWO}
                disabled={editWOBusy || (editWOReassignCheck?.allowed === false)}
              >
                {editWOBusy ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Void Modal */}
      {voidWO && (
        <WOVoidModal
          open={!!voidWO}
          onOpenChange={(o) => !o && setVoidWO(null)}
          wo={voidWO}
          woLines={woLines ?? []}
          onComplete={() => { setVoidWO(null); refreshAll(); }}
        />
      )}

      {/* Close Modal */}
      {closeWO && (
        <WOCloseModal
          open={!!closeWO}
          onOpenChange={(o) => !o && setCloseWO(null)}
          wo={closeWO}
          woLines={woLines ?? []}
          onComplete={() => { setCloseWO(null); refreshAll(); }}
        />
      )}
      {/* Create Bill Dialog */}
      {billWO && (
        <WOCreateBillDialog
          open={!!billWO}
          onOpenChange={(o) => !o && setBillWO(null)}
          wo={billWO}
          woLines={woLines ?? []}
          projectId={projectId}
          onCreated={() => { setBillWO(null); refreshAll(); }}
        />
      )}

      <AlertDialog open={!!deleteWO} onOpenChange={(o) => !o && setDeleteWO(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Permanently Delete Work Order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{deleteWO?.number}</strong> and all its lines, bills, and approval requests from the database. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingWO}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletingWO}
              onClick={handleDeleteWO}
            >
              {deletingWO ? "Deleting..." : "Delete Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
